import React from 'react';
import css from '@emotion/css';
import { PageInfo } from '../../../../libs/paging';
import { SortActionType } from '../../../../libs/filter';
import { formatIntNumber, formatDate } from '../../../../libs/format';
import { InstagramStoryPostSortField, ORDER } from '../../../../__generated__/globalTypes';
import AnalyticsStoryPostDetail from '../AnalyticsStoryPostDetail';
import * as List from '../../../atoms/List';
import CarouselModal from '../../../molecules/CarouselPopup';
import { SliderComponents, SliderCss, StyledComponents } from '../../../molecules/SliderTable';
import PostColumn from '../PostColumn';
import { usePostDetailModal } from '../../AnalyticsShared/BasePostList/usePostDetailModal';
import { AllInstagramStoryPosts_allInstagramStoryPosts } from './__generated__/AllInstagramStoryPosts';
import { getAllStoryPostsHeaders } from './utils';

export type InstagramStoryPostsSortAction = SortActionType<InstagramStoryPostSortField>;

export interface InstagramStoryPostsListProps extends InstagramStoryPostsSortAction {
  instagramStoryPosts: AllInstagramStoryPosts_allInstagramStoryPosts[];
  pageInfo: PageInfo;
  accountId: number;
}

const InstagramStoryPostsList = (props: InstagramStoryPostsListProps) => {
  const {
    sort: { field, order },
    setSort,
    instagramStoryPosts,
    pageInfo: { firstIndex, lastIndex, totalCount, currentPage, totalPages },
    accountId
  } = props;
  const {
    openPostDetail,
    siblingPostId,
    handleClosePostDetail,
    handleClickPost,
    handleClickNext,
    handleClickPrev
  } = usePostDetailModal(instagramStoryPosts);

  return (
    <>
      <StyledComponents.ButtonWrapper css={SliderCss.boderTop}>
        <SliderComponents.PageCount total={totalCount} countLabel="Posts" />
      </StyledComponents.ButtonWrapper>
      <SliderComponents.Wrapper css={[SliderCss.marginTop, SliderCss.clearPadding]}>
        <SliderComponents.Container>
          <SliderComponents.SliderSection>
            <SliderComponents.SliderTable>
              <thead>
                <SliderComponents.HeaderRow css={SliderCss.bgndColor}>
                  <SliderComponents.StyckyHeaderDivWrapper>
                    <List.HeaderColumn
                      title="Story Post"
                      css={[SliderCss.clearPadding, SliderCss.width245, SliderCss.flexStart]}
                      isTable={false}
                    />
                  </SliderComponents.StyckyHeaderDivWrapper>
                  {/* slider */}
                  {getAllStoryPostsHeaders().map(col => (
                    <List.HeaderColumn
                      key={col.columnName}
                      title={col.title}
                      help={col.help}
                      activeAsc={field === col.orderField && order === ORDER.ASC}
                      activeDesc={field === col.orderField && order === ORDER.DESC}
                      sortAsc={() => setSort({ field: col.orderField, order: ORDER.ASC })}
                      sortDesc={() => setSort({ field: col.orderField, order: ORDER.DESC })}
                      css={col.css}
                    />
                  ))}
                </SliderComponents.HeaderRow>
              </thead>
              <tbody>
                {instagramStoryPosts.map(item => {
                  const {
                    impressions,
                    reach,
                    comments,
                    exits,
                    tapsForward,
                    tapsBack,
                    postDate,
                    id,
                    thumbNail,
                    content
                  } = item;

                  return (
                    <StyledComponents.StyledRowNew key={id} css={[SliderCss.paddingSides, SliderCss.rowHeight62]}>
                      <SliderComponents.StickyBodyRowWrapper css={SliderCss.clearPadding}>
                        <PostColumn
                          key={id}
                          id={id}
                          src={thumbNail}
                          title={content}
                          onClick={() => handleClickPost(id)}
                        />
                      </SliderComponents.StickyBodyRowWrapper>
                      {/* sliding section */}
                      <StyledComponents.NumberColumn data={formatIntNumber(impressions)} />
                      <StyledComponents.NumberColumn data={formatIntNumber(reach)} />
                      <StyledComponents.NumberColumn data={formatIntNumber(comments)} />
                      <StyledComponents.NumberColumn data={formatIntNumber(exits)} />
                      <StyledComponents.NumberColumn data={formatIntNumber(tapsForward)} />
                      <StyledComponents.NumberColumn data={formatIntNumber(tapsBack)} />
                      <List.TextColumn data={formatDate(postDate)} css={rightAlignedCol} />
                    </StyledComponents.StyledRowNew>
                  );
                })}
              </tbody>
            </SliderComponents.SliderTable>
          </SliderComponents.SliderSection>
        </SliderComponents.Container>
      </SliderComponents.Wrapper>
      <SliderComponents.Pager
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        first={firstIndex}
        last={lastIndex}
      />
      <CarouselModal
        open={!!openPostDetail}
        title=""
        cancel="OK"
        onClose={handleClosePostDetail}
        onClickNext={handleClickNext}
        onClickPrev={handleClickPrev}
        siblingPostId={siblingPostId}
      >
        <AnalyticsStoryPostDetail closeModal={handleClosePostDetail} accountId={accountId} />
      </CarouselModal>
    </>
  );
};

const rightAlignedCol = css`
  & div {
    justify-content: flex-end;
  }
`;

export default InstagramStoryPostsList;
